/**
 * Pilot Discoveries — Dashboard card surfacing the freshest auto-scan
 * findings from the weekly Pipeline Dedup cron + (extensible) any other
 * world-class auto-discoveries we add later.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { GitMerge, Sparkles, ArrowUpRight, AlertOctagon, Layers, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) { return `$${(n || 0).toLocaleString()}`; }

export default function PilotDiscoveriesCard() {
  const [finding, setFinding] = useState(null);
  const [oktaDiscoveries, setOktaDiscoveries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [pRes, oRes] = await Promise.all([
        fetch(`${API}/pipeline-dedup/latest-cron-finding`, { headers }),
        fetch(`${API}/okta-discovery/recent-discoveries?limit=3`, { headers }),
      ]);
      if (pRes.ok) setFinding((await pRes.json()).finding);
      if (oRes.ok) setOktaDiscoveries((await oRes.json()).discoveries || []);
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!loaded) {
    return <Card className="bg-slate-900 border-slate-800/60 animate-pulse" data-testid="pilot-disc-loading"><CardContent className="p-6 h-32" /></Card>;
  }

  const hasPipeline = !!finding;
  const hasOkta = oktaDiscoveries.length > 0;

  // Pure-empty state: no scan AND no Okta discoveries
  if (!hasPipeline && !hasOkta) {
    return (
      <Card className="bg-slate-950 border-emerald-500/30" data-testid="pilot-disc-empty">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="inline-flex w-10 h-10 rounded-lg bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
              <GitMerge className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-bold text-white">Pilot Discoveries</h3>
                <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest">WEEKLY</Badge>
              </div>
              <p className="text-sm text-slate-100 mb-3">Connect Airflow, Dagster, or Okta and CARTIE will <span className="text-emerald-300 font-semibold">auto-scan + flag overlaps</span> every week — without you clicking anything.</p>
              <div className="flex gap-4">
                <Link to="/admin/pipeline-dedup" className="inline-flex items-center gap-1 text-sm text-emerald-300 hover:text-emerald-200 font-semibold" data-testid="pilot-disc-connect-pipelines-link">
                  Connect Pipelines <ArrowUpRight className="w-3 h-3" />
                </Link>
                <Link to="/admin/saas-spend" className="inline-flex items-center gap-1 text-sm text-cyan-300 hover:text-cyan-200 font-semibold" data-testid="pilot-disc-connect-okta-link">
                  Connect Okta <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Has-data state: render whichever discoveries we have
  const pipelineClusters = (finding?.clusters || []).slice(0, 2);
  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="pilot-disc-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-300" />
              Pilot Discoveries
              {hasPipeline && <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest">{finding.week_iso}</Badge>}
            </CardTitle>
            <p className="text-xs text-slate-100 mt-1">
              {hasPipeline && <>{finding.clusters_count} duplicate DAG cluster{finding.clusters_count !== 1 ? 's' : ''} · <span className="text-emerald-300 font-bold">{fmt(finding.annual_recoverable_usd)}/yr recoverable</span></>}
              {hasPipeline && hasOkta && <> · </>}
              {hasOkta && <>{oktaDiscoveries.length} new Okta-discovered app{oktaDiscoveries.length !== 1 ? 's' : ''}</>}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {hasPipeline && <Link to="/admin/pipeline-dedup" className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 text-xs group font-semibold" data-testid="pilot-disc-view-link"><GitMerge className="w-3 h-3" /> Pipelines <ArrowUpRight className="w-3 h-3" /></Link>}
            {hasOkta && <Link to="/admin/saas-spend" className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 text-xs group font-semibold" data-testid="pilot-disc-saas-link"><Layers className="w-3 h-3" /> SaaS <ArrowUpRight className="w-3 h-3" /></Link>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Pipeline cluster cards */}
          {pipelineClusters.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {pipelineClusters.map((c, i) => (
                <div key={i} className="rounded-md bg-slate-900 border border-emerald-500/20 p-3" data-testid={`pilot-disc-cluster-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <Badge className={`text-[9px] font-bold uppercase tracking-widest ${
                      c.max_severity === 'high' ? 'bg-rose-500/20 text-rose-200'
                      : c.max_severity === 'medium' ? 'bg-amber-500/20 text-amber-200'
                      : 'bg-cyan-500/20 text-cyan-200'
                    }`}>{c.max_severity}</Badge>
                    <span className="text-xs text-emerald-300 font-mono font-bold">−${c.estimated_monthly_recoverable_usd}/mo</span>
                  </div>
                  <div className="text-xs text-slate-100 mb-1">
                    <span className="text-emerald-300 font-bold">KEEP:</span> <span className="font-mono text-white">{c.primary}</span>
                  </div>
                  <div className="text-xs text-slate-200 truncate">
                    <span className="text-rose-300 font-bold">DELETE:</span> <span className="font-mono">{(c.duplicates || []).slice(0, 3).join(', ')}</span>
                    {(c.duplicates || []).length > 3 && <span className="text-slate-300"> +{(c.duplicates || []).length - 3} more</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Okta-discovered SaaS cards */}
          {oktaDiscoveries.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest pt-1">New from Okta</div>
              {oktaDiscoveries.map((d, i) => (
                <div key={i} className="rounded-md bg-slate-900 border border-cyan-500/30 px-3 py-2 flex items-start justify-between gap-3" data-testid={`pilot-disc-okta-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-bold text-white">{d.vendor}</span>
                      <Badge className="bg-slate-800 border border-slate-600 text-slate-100 text-[10px] font-mono">{d.category}</Badge>
                      {d.needs_cost_data && <Badge className="bg-amber-500/20 text-amber-200 border border-amber-400/40 text-[9px]">needs cost</Badge>}
                    </div>
                    {d.potential_overlap_count > 0 ? (
                      <div className="text-xs text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Potential overlap with {d.potential_overlap_count} existing app{d.potential_overlap_count !== 1 ? 's' : ''}: {d.potential_overlap_vendors.slice(0, 3).join(', ')}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-200">No overlap detected · category: {d.category}</div>
                    )}
                  </div>
                  {d.potential_overlap_monthly_cost_usd > 0 && (
                    <div className="text-xs text-amber-300 font-mono font-bold shrink-0">{fmt(d.potential_overlap_monthly_cost_usd)}/mo at risk</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {hasPipeline && <div className="text-[10px] text-slate-300 mt-3 italic">Auto-scanned {new Date(finding.scanned_at).toLocaleDateString()} via your connected Airflow/Dagster connectors.</div>}
      </CardContent>
    </Card>
  );
}
